import { RootDispatch, RootGetState } from "../types";
import {
  SecureMessageStep,
  ParticipantDobVerfication,
  ParticipantRegistration,
  ParticipantAuthentication
} from "./types";
import { get, post } from "../../utils/fetch";
import { ParsedResponse, getError } from "../../utils/responses";
import { FormikErrors } from "formik";
import moment from "moment";
import { AxiosResponse } from "axios";
import { setParticipantUser } from "../session/actions";
import { ParticipantUser } from "../session/types";
import { Conversation } from "../messaging/types";
import { subscribeChannels } from "../pubnub/actions";

export type Action =
  | {
      type: "secureMessage/setRouteState";
      messageId: string;
      pinSet: boolean;
      participantType: string;
    }
  | { type: "secureMessage/setSecureMessageStep"; step: SecureMessageStep }
  | { type: "secureMessage/forgotPin"; step: SecureMessageStep; pinSet: boolean }
  | { type: "secureMessage/getConversation-started" }
  | { type: "secureMessage/getConversation-success"; conversation: Conversation }
  | { type: "secureMessage/getConversation-failure"; errorMessage: string }
  | { type: "secureMessage/verifyDateOfBirth-started" }
  | { type: "secureMessage/verifyDateOfBirth-success"; dateOfBirth: string }
  | { type: "secureMessage/verifyDateOfBirth-failure"; errorMessage: string }
  | { type: "secureMessage/registerParticipant-started" }
  | { type: "secureMessage/registerParticipant-success" }
  | { type: "secureMessage/registerParticipant-failure"; errorMessage: string }
  | { type: "secureMessage/authenticateParticipant-started" }
  | { type: "secureMessage/authenticateParticipant-success" }
  | { type: "secureMessage/authenticateParticipant-failure"; errorMessage: string };

export const setRouteState = (messageId: string, pinSet: boolean, participantType: string) => (
  dispatch: RootDispatch
) => {
  dispatch({
    type: "secureMessage/setRouteState",
    messageId: messageId,
    pinSet: pinSet,
    participantType: participantType
  });
  dispatch(getConversation(messageId));
};

export const setSecureMessageStep = (step: SecureMessageStep) => (dispatch: RootDispatch) => {
  dispatch({
    type: "secureMessage/setSecureMessageStep",
    step: step
  });
};

export const forgotPin = () => (dispatch: RootDispatch) => {
  dispatch({
    type: "secureMessage/forgotPin",
    step: SecureMessageStep.ValidateDob,
    pinSet: false
  });
};

export const validateDateOfBirth = (
  params: ParticipantDobVerfication,
  onError: (error: FormikErrors<ParticipantDobVerfication>) => void
) => async (dispatch: RootDispatch, getState: RootGetState) => {
  dispatch({
    type: "secureMessage/verifyDateOfBirth-started"
  });

  const url = "/api/participants/verifyDateOfBirth/";
  const body = { dateOfBirth: moment(params.dateOfBirth).format("YYYY-MM-DD") };

  const resp = await post<AxiosResponse>(url, body);

  if (resp.error) {
    dispatch({
      type: "secureMessage/verifyDateOfBirth-failure",
      errorMessage: resp.error.message
    });
    onError(resp.error.fieldErrors as FormikErrors<ParticipantDobVerfication>);
  } else if (resp.status === 200) {
    dispatch({
      type: "secureMessage/verifyDateOfBirth-success",
      dateOfBirth: body.dateOfBirth
    });
    if (getState().securemessage.data.pinSet) {
      dispatch({
        type: "secureMessage/setSecureMessageStep",
        step: SecureMessageStep.ValidatePassword
      });
    } else {
      dispatch({
        type: "secureMessage/setSecureMessageStep",
        step: SecureMessageStep.SetPassword
      });
    }
  } else {
    // TODO logging
  }
};

export const registerParticipant = (
  params: ParticipantRegistration,
  onError: (error: FormikErrors<ParticipantRegistration>) => void
) => async (dispatch: RootDispatch, getState: RootGetState) => {
  dispatch({
    type: "secureMessage/registerParticipant-started"
  });

  const url = "/api/participants/register/";

  const resp = await post<AxiosResponse<ParticipantUser>>(url, params);

  if (resp.error) {
    dispatch({
      type: "secureMessage/registerParticipant-failure",
      errorMessage: resp.error.message
    });
    onError(resp.error.fieldErrors as FormikErrors<ParticipantRegistration>);
  } else if (resp.status === 200) {
    dispatch(setParticipantUser(resp.data));
    dispatch({ type: "secureMessage/registerParticipant-success" });
    dispatch(getConversation(getState().securemessage.data.messageId));
  } else {
    // TODO logging
  }
};

export const authenticateParticipant = (
  params: ParticipantAuthentication,
  onError: (error: FormikErrors<ParticipantAuthentication>) => void
) => async (dispatch: RootDispatch, getState: RootGetState) => {
  dispatch({
    type: "secureMessage/authenticateParticipant-started"
  });

  const url = "/api/participants/authenticate/";

  const resp = await post<AxiosResponse<ParticipantUser>>(url, params);

  if (resp.error) {
    dispatch({
      type: "secureMessage/authenticateParticipant-failure",
      errorMessage: resp.error.message ? resp.error.message : "PIN is invalid."
    });
    onError(resp.error.fieldErrors as FormikErrors<ParticipantAuthentication>);
  } else if (resp.status === 200) {
    dispatch(setParticipantUser(resp.data));
    dispatch({ type: "secureMessage/authenticateParticipant-success" });
    dispatch(getConversation(getState().securemessage.data.messageId));
    dispatch(subscribeChannels([getState().session.data.currentUser?.id]));
  } else {
    // TODO logging
  }
};

export const getConversation = (id: string) => async (
  dispatch: RootDispatch,
  getState: RootGetState
) => {
  dispatch({
    type: "secureMessage/getConversation-started"
  });

  const url = `/api/messages/${id}/`;

  async function getParsedResponse<T>(resp: AxiosResponse) {
    if (resp.status >= 200 && resp.status < 300) {
      return { status: resp.status, error: null, data: resp.data };
    }
    return { status: resp.status, error: await getError(resp), data: null };
  }
  const resp = await get<Conversation>(url, {}, getParsedResponse);

  // this is intentional. Calling on initial load to see if we aren't already authorized.
  if (resp.status === 403) {
    getState().securemessage.data.pinSet
      ? dispatch(setSecureMessageStep(SecureMessageStep.ValidatePassword))
      : dispatch(setSecureMessageStep(SecureMessageStep.ValidateDob));
  } else if (resp.status === 404) {
    dispatch(setSecureMessageStep(SecureMessageStep.InvalidSession));
  } else if (resp.error) {
    dispatch({
      type: "secureMessage/getConversation-failure",
      errorMessage: resp.error.message
    });
  } else if (resp.data) {
    dispatch({
      type: "secureMessage/getConversation-success",
      conversation: resp.data
    });
    dispatch(setSecureMessageStep(SecureMessageStep.ViewMessage));
  } else {
    // TODO logging
  }
};
